<template>
  <div :class="['container-fluid', isMobile ? 'px-0' : '']">
    <ValidationObserver ref="createEditForm">
      <div class="row d-flex justify-content-start">
        <div class="col-12">
          <SectionTitle has-line content="Información de Formulario" />
        </div>
      </div>

      <div class="row d-flex justify-content-start">
        <div class="col-12 col-md-8">
          <ValidationProvider v-slot="{ errors }" rules="required">
            <custom-input
              v-model="value.name"
              label="Nombre de Parámetro:"
              :error="errors[0]"
              :custom-error-msg="getError(25001)"
            />
          </ValidationProvider>
        </div>

        <div :class="['col-12 col-md-4', isMobile ? 'pt-2' : '']">
          <custom-drop-down
            v-model="value.academicYear"
            :options="academicYears"
            label="Año Académico:"
            fieldkey="id"
            fieldname="description"
            placeholder="Seleccione"
            class="height-fixed"
          />
        </div>
      </div>

      <div v-show="!isMobile" class="row py-2" />

      <div
        :class="['row d-flex justify-content-start', isMobile ? 'pt-2' : '']"
      >
        <div class="col-12">
          <ValidationProvider v-slot="{ errors }" rules="required">
            <custom-input
              v-model="value.description"
              label="Descripción del Parámetro:"
              :error="errors[0]"
              :custom-error-msg="getError(25003)"
            />
          </ValidationProvider>
        </div>
      </div>

      <div :class="['row py-2', isMobile ? 'py-3' : '']" />

      <div
        :class="[
          'row d-flex justify-content-start grey-bg-row',
          isMobile ? '' : 'p-4',
        ]"
      >

        <div :class="['col-12 col-md-3', { 'pt-2': isMobile }]">
          <ValidationProvider
            v-slot="{ errors }"
            vid="typeCode"
            rules="required"
          >
            <custom-drop-down
              v-model="value.typeCodeId"
              :options="typeCodes"
              label="Tipo de Parámetro:"
              :regular-label="isMobile"
              fieldkey="id"
              fieldname="name"
              placeholder="Seleccione"
              class="height-fixed"
              :error="errors[0]"
              :custom-error-msg="getError(25009)"
              @input="typeCodeHandler"
            />
          </ValidationProvider>
        </div>

        <div :class="['col-12 col-md-3', { 'pt-2': isMobile }]">
          <ValidationProvider
            v-slot="{ errors }"
            vid="dataTypeCode"
            rules="required"
          >
            <custom-drop-down
              v-model="value.dataTypeCodeId"
              :options="dataTypeCodes"
              label="Tipo de Data del Parámetro:"
              :disabled="!value.typeCodeId"
              :regular-label="isMobile"
              fieldkey="id"
              fieldname="name"
              placeholder="Seleccione"
              class="height-fixed"
              :error="errors[0]"
              :custom-error-msg="getError(25010)"
              @input="refreshVisibilityFields"
            />
          </ValidationProvider>
        </div>

        <div class="col-12 col-md-3" v-if="canShowEffectiveDate">
          <ValidationProvider
            v-slot="{ errors }"
            vid="effectiveStartDate"
            rules="required"
          >
            <custom-date-picker
              v-model="value.effectiveDate"
              :class="{ 'pt-2': isMobile }"
              :use-utc="false"
              format="dd/MM/yyyy"
              label="Fecha Comienzo de Efectividad"
              placeholder="DD/MM/YYYY"
              :error="errors[0]"
              :custom-error-msg="getError(25004)"
            />
          </ValidationProvider>
        </div>

        <div :class="['col-12 col-md-3', { 'pt-2': isMobile }]" v-if="canShowEffectiveDate">
          <ValidationProvider
            v-slot="{ errors }"
            rules="endDateRequired|endDateRule:@effectiveStartDate"
          >
            <custom-date-picker
              v-model="value.effectiveEndDate"
              :use-utc="false"
              format="dd/MM/yyyy"
              label="Fecha Final de Efectividad"
              placeholder="DD/MM/YYYY"
              :error="errors[0]"
              :custom-error-msg="getError(errors[0])"
            />
          </ValidationProvider>
        </div>        

        <div :class="['col-12 col-md-3', { 'pt-2': isMobile }]" v-if="canShowRanges">
          <template v-if="isDateTypeCode">
            <ValidationProvider
              v-slot="{ errors }"
              :rules="`${rangeRequired ? 'required' : ''}`"
              vid="rangeMinimumDate"
            >
              <custom-date-picker
                v-model="value.rangeMinimumValue"
                :use-utc="false"
                format="dd/MM/yyyy"
                label="Valor Mínimo de Rango:"
                placeholder="DD/MM/YYYY"
                :error="errors[0]"
                :custom-error-msg="getError(25008)"
                :disabled="!rangeRequired"
              />
            </ValidationProvider>
          </template>

          <template v-else>
            <ValidationProvider
              v-slot="{ errors }"
              :rules="`${rangeRequired ? 'required' : ''}`"
            >
              <custom-input
                v-model="value.rangeMinimumValue"
                label="Valor Mínimo de Rango:"
                :class="
                  !rangeRequired
                    ? 'width-container read-only'
                    : 'width-container'
                "
                :error="errors[0]"
                :custom-error-msg="getError(25008)"
              />
            </ValidationProvider>
          </template>
        </div>

        <div :class="['col-12 col-md-3', { 'pt-2': isMobile }]" v-if="canShowRanges">
          <template v-if="isDateTypeCode">
            <ValidationProvider
              v-slot="{ errors }"
              :rules="
                `${
                  rangeRequired
                    ? 'required|rangeDateRule:@rangeMinimumDate'
                    : ''
                }`
              "
              :custom-messages="{
                required: 'El valor máximo de rango es requerido',
                rangeDateRule:
                  'La fecha del valor máximo de rango debe ser mayor a la fecha del valor mínimo.',
              }"
            >
              <custom-date-picker
                v-model="value.rangeMaximumValue"
                :use-utc="false"
                format="dd/MM/yyyy"
                label="Valor Máximo de Rango:"
                placeholder="DD/MM/YYYY"
                :error="errors[0]"
                :custom-error-msg="errors[0]"
                :disabled="!rangeRequired"
              />
            </ValidationProvider>
          </template>

          <template v-else>
            <ValidationProvider
              v-slot="{ errors }"
              :rules="`${rangeRequired ? 'required' : ''}`"
            >
              <custom-input
                v-model="value.rangeMaximumValue"
                label="Valor Máximo de Rango:"
                :error="errors[0]"
                :class="[
                  !rangeRequired
                    ? 'width-container read-only'
                    : 'width-container',
                  { 'w-100': isMobile },
                ]"
                :custom-error-msg="getError(25007)"
              />
            </ValidationProvider>
          </template>
        </div>

        <div :class="['col-12 col-md-3', { 'py-2': isMobile }]" v-if="staticValueRequired">
          <template v-if="isDateTypeCode">
            <ValidationProvider
              v-slot="{ errors }"
              :rules="staticValueRequired ? 'required' : ''"
            >
              <custom-date-picker
                v-model="value.staticValue"
                :use-utc="false"
                format="dd/MM/yyyy"
                label="Valor Estático:"
                placeholder="DD/MM/YYYY"
                :error="errors[0]"
                :custom-error-msg="getError(25006)"
                :disabled="!staticValueRequired"
              />
            </ValidationProvider>
          </template>

          <template v-else>
            <ValidationProvider
              v-slot="{ errors }"
              :rules="
                `${
                  staticValueRequired
                    ? 'staticValueRequired|staticValueRule:@typeCode,@dataTypeCode'
                    : ''
                }`
              "
            >
              <custom-input
                v-model="value.staticValue"
                label="Valor Estático:"
                :class="!staticValueRequired ? 'read-only' : ''"
                :error="errors[0]"
                :custom-error-msg="getError(errors[0])"
              />
            </ValidationProvider>
          </template>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate';
import SectionTitle from 'SectionTitle';
import CustomInput from 'CustomInput';
import CustomDropDown from 'custom-drop-down';
import CustomDatePicker from 'custom-date-picker';

import typeCodes from '@/utils/constants/sysParamCodeTypes/typeCodes';
import dataTypeCodes from '@/utils/constants/sysParamCodeTypes/dataTypeCodes';

import moment from 'moment';

// Extended rules
extend('endDateRule', {
  params: ['target'],
  validate(value, { target }) {
    if (!target) {
      return true;
    }

    return moment(target).isBefore(value);
  },
  message: '25013',
});

extend('rangeDateRule', {
  params: ['target'],
  validate(value, { target }) {
    if (!target) {
      return true;
    }

    return moment(target).isBefore(value);
  },
});

extend('endDateRequired', {
  validate(value) {
    return {
      required: true,
      valid: ['', null, undefined].indexOf(value) === -1,
    };
  },
  computesRequired: true,
  message: '25005',
});

extend('staticValueRequired', {
  validate(value) {
    return {
      required: true,
      valid: ['', null, undefined].indexOf(value) === -1,
    };
  },
  computesRequired: true,
  message: '25006',
});

extend('staticValueRule', {
  params: ['typeCode', 'dataTypeCode'],
  validate(value, { typeCode, dataTypeCode }) {
    if (typeCode?.id === typeCodes.STATIC) {
      if (dataTypeCode?.id == dataTypeCodes.BOOL) {
        return value === 'true' || value === 'false';
      } else if (
        dataTypeCode?.id == dataTypeCodes.INT ||
        dataTypeCode?.id == dataTypeCodes.NUMERIC
      ) {
        return /^\d+$/.test(value);
      }
    }
    return true;
  },
  message: '25015',
});
// Extended rules

export default {
  name: 'FormInformation',
  components: {
    ValidationObserver,
    ValidationProvider,
    SectionTitle,
    CustomInput,
    CustomDropDown,
    CustomDatePicker,
  },
  data() {
    return {
      canShowEffectiveDate: true,      
      canShowRanges: true      
    };
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    academicYears: {
      type: [Object, Array],
      default: () => [],
    },
    typeCodes: {
      type: [Object, Array],
      default: () => [],
    },
    dataTypeCodes: {
      type: [Object, Array],
      default: () => [],
    },
    resources: {
      type: [Object, Array],
      default: () => [],
    },
  },
  computed: {
    rangeRequired() {
      if (this.value.typeCodeId?.id === typeCodes.RANGE) {
        return true;
      }

      return false;
    },
    staticValueRequired() {
      if (
        this.value.typeCodeId?.id === typeCodes.STATIC ||
        this.value.typeCodeId?.id === typeCodes.MULTIPLE_VALUE
      ) {
        return true;
      }

      return false;
    },
    isEditMode() {
      return this.$route.params.id ? true : false;
    },
    isMobile() {
      return this.$store.getters.getIsMobileDensity;
    },
    isDateTypeCode() {
      return this.value.dataTypeCodeId?.id === dataTypeCodes.DATE;
    },    
  },
  watch: {   
    'value.typeCodeId':{
      handler(){          
          this.refreshVisibilityFields()
      }
    }
  },
  methods: {
    refreshVisibilityFields(){           
      this.canShowEffectiveDate = this.value.typeCodeId?.id === typeCodes.MULTIPLE_VALUE ||
                                      (this.value.typeCodeId?.id === typeCodes.RANGE && this.value.dataTypeCodeId?.id === dataTypeCodes.DATE);

      this.canShowRanges = this.value.typeCodeId?.id === typeCodes.MULTIPLE_VALUE ||
                                      (this.value.typeCodeId?.id === typeCodes.RANGE && (this.value.dataTypeCodeId?.id === dataTypeCodes.INT || 
                                                                                         this.value.dataTypeCodeId?.id === dataTypeCodes.NUMERIC || 
                                                                                         this.value.dataTypeCodeId?.id === dataTypeCodes.VARCHAR));                  
    },
    typeCodeHandler(val) {
      this.value.dataTypeCodeId = null;
      if (val?.id === typeCodes.RANGE) {
        this.value.staticValue = null;

        this.$emit('hide-bool');
      } else if (
        val?.id === typeCodes.STATIC ||
        val?.id === typeCodes.MULTIPLE_VALUE
      ) {
        this.value.rangeMinimumValue = null;
        this.value.rangeMaximumValue = null;

        this.$emit('refresh-data-type-codes');
      }    
    },
    async validate() {
      return await this.$refs.createEditForm.validate();
    },
    getError(code) {
      let message = this.resources[code];

      return message ? message.replace(`(${code})`, '') : '';
    },
  },
};
</script>

<style scoped>
.grey-bg-row {
  background: #f7f7f7 0% 0% no-repeat padding-box;
  border-radius: 5px;
  padding: 10px 0 15px 0;
}

.read-only >>> input {
  pointer-events: none;
  cursor: not-allowed !important;
  background-color: #f8f8f8;
}

.height-fixed >>> .vs__dropdown-toggle {
  height: calc(1.5em + 0.75rem + 2px) !important;
}

@media only screen and (max-width: 375px) {
  .grey-bg-row {
    margin: 0;
  }

  ::v-deep .data-label {
    font-size: 12px !important;
    color: #727272;
    font-weight: normal;
  }

  ::v-deep .data-name {
    font-size: 12px !important;
    color: #727272;
    font-weight: normal;
  }

  ::v-deep .vs__search {
    font-size: 12px !important;
  }

  ::v-deep .vs__selected {
    font-size: 12px !important;
  }

  ::v-deep .vs__dropdown-menu {
    font-size: 12px !important;
  }
}
</style>
